'use client';

import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { AuthResponse } from '@/types/common';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface OrgStatusResponse {
  data: {
    IsTemplateCreated: boolean;
    IsProjectCreated: boolean;
  };
}

interface UseLoginSubmit {
  isLoading: boolean;
  error: string;
  handleSubmit: (formData: LoginFormData) => Promise<boolean>;
}

export const useLoginSubmit = (): UseLoginSubmit => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleSubmit = async (formData: LoginFormData): Promise<boolean> => {
    setIsLoading(true);
    setError('');
    await new Promise((resolve) => setTimeout(resolve, 2000));

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          Email: formData.email,
          Password: formData.password,
          RememberMe: formData.rememberMe,
        }),
      });

      const data = (await response.json()) as AuthResponse;

      if (data.error) {
        setError(data.error);
        return false;
      }

      if (data.result) {
        if (formData.rememberMe) {
          localStorage.setItem('savedEmail', formData.email);
        } else {
          localStorage.removeItem('savedEmail');
        }

        // tokenをlocalStorageに保存
        if (data.Token) {
          localStorage.setItem('token', data.Token);
        }

        // organizationIDをlocalStorageに保存
        if (data.OrganizationID) {
          localStorage.setItem('organizationId', JSON.stringify(data.OrganizationID));
        }

        // userIDをlocalStorageに保存
        if (data.UserID) {
          localStorage.setItem('userId', JSON.stringify(data.UserID));
        }

        // roleIDをlocalStorageに保存
        if (data.RoleID) {
          localStorage.setItem('roleId', JSON.stringify(data.RoleID));
        }

        try {
          const statusResponse = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}/organization/onboarding-status`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          if (statusResponse.ok) {
            const statusData = (await statusResponse.json()) as OrgStatusResponse;

            if (statusData.data.IsTemplateCreated && statusData.data.IsProjectCreated) {
              router.push('/projects');
            } else {
              router.push('/onboarding');
            }
          } else {
            router.push('/not-found');
          }
        } catch {
          router.push('/not-found');
        }

        return true;
      }

      setError('ログインに失敗しました');
      return false;
    } catch {
      setError('ログインに失敗しました');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    handleSubmit,
  };
};
